import React, { useState, useEffect } from 'react';
import Welcome from './components/Welcome';
import TextBrowser from './components/TextBrowser';
import Reader from './components/Reader';
import SRSReview from './components/SRSReview';
import SettingsScreen from './components/SettingsScreen';
import BackHeader from './components/BackHeader';
import Login from './components/Login';
import { AuthProvider, useAuth } from './hooks/useAuth';
import { useVocab } from './hooks/useVocab';
import { useFontScale } from './hooks/useFontScale';
import { isDue } from './utils/anki';
import { startCloudSync } from './utils/cloudSync';
import { startVocabSync } from './utils/vocabSync';
import { STATIC } from './staticMode';
// Visible build marker so you can SEE which bundle is loaded. Generated from the git commit
// count by scripts/stamp_build.js (npm "prebuild"), so it changes on every ship — a
// hardcoded one silently stays put and tells you nothing.
import { BUILD_ID } from './buildId';
import './App.css';

export { BUILD_ID };



// 關於 — ports the phone AboutScreen verbatim.
function About({ onBack }) {
  return (
    <div className="about">
      <BackHeader title="關於" onBack={onBack} />
      <div className="ab-scroll">
        <h3 className="ab-h ab-h0">名稱由來</h3>
        <p className="ab-p">
          3S 是族名的三種拼法：Seediq、Sediq、Seejiq。
          4T 是四個群名：Tgdaya（德固達雅）、Toda（都達）、Truku（德路固）、Taroko（太魯閣）。
        </p>

        <h3 className="ab-h">名稱</h3>
        <p className="ab-p">
          聖經稱為 Kari Utux Baro／Baraw（上帝的話，提後3:16）。
          書卷名稱皆取自經文本身，並非新造詞：
          kari（話）、pnsdhegan／pnsdhgan／pnsdhuwan（約）、
          bgurah／burah（新，來8:13）、cmucac／smudal（舊，林後3:14）。
        </p>

        <h3 className="ab-h">收錄內容</h3>
        <p className="ab-p">
          舊約與新約的重要篇章：故事、講論、書信與詩歌，共 56 篇。
          分為六個書架——舊約、新約各以 Tgdaya（德固達雅）、Truku（太魯閣）、
          Toda（都達）閱讀；每個書架裡就是這些篇章。Toda 與 Truku 待補。
        </p>

        <h3 className="ab-h">對照譯本</h3>
        <p className="ab-p">
          族語經文底下可並列中文（現中2019 或 和合本2010）、英文（GNB 或 KJV），
          以及另一種族語——三者可任意搭配或都不顯示。請至「設定 · 對照譯本」選擇。
        </p>

        <h3 className="ab-h">功能</h3>
        <p className="ab-li">· 三種閱讀層級（由短至長）：句（子句）、節（整節）、文（整篇）。</p>
        <p className="ab-li">· 真人朗讀，隨音檔逐詞高亮，並顯示波形。</p>
        <p className="ab-li">· 點擊單詞查看字典解釋，標記為學習中或已知。</p>
        <p className="ab-li">· 「學習中」的詞會進入間隔複習。</p>
        <p className="ab-li">· 讀完可於文末申請加入想讀的經文。</p>

        <h3 className="ab-h">語音</h3>
        <p className="ab-li">· 新約（Tgdaya）：真人朗讀，逐節對齊，附波形。</p>
        <p className="ab-li">· 舊約（Tgdaya）：語音待補，目前為純文字。</p>
        <p className="ab-li">· Truku、Toda：待補。</p>

        <h3 className="ab-h">經文版權</h3>
        <p className="ab-p">聖經經文版權屬台灣聖經公會等權利人所有，本站僅供族語學習使用。</p>

        <h3 className="ab-h">圖示</h3>
        <div className="ab-icon-row">
          <img className="ab-icon" src={process.env.PUBLIC_URL + '/tattoo.png'} alt="" />
          <p className="ab-icon-text">
            紋面（patis）象徵 Hako Utux 靈魂之橋：真正的男女在生命盡頭跨越，回返祖靈之地。patis 原指紋面，今亦指書寫。
          </p>
        </div>

        <h3 className="ab-h">意見回饋</h3>
        <p className="ab-p">歡迎來信：<a className="ab-mail" href="mailto:shidailun@gmail.com">shidailun@gmail.com</a></p>

        <p className="ab-note">語言保存計畫</p>
      </div>
    </div>
  );
}

function AppInner() {
  const { user, ready, logout } = useAuth();
  const { getLearningWords } = useVocab();
  const [fontScale, setFontScale] = useFontScale();
  const [screen, setScreen] = useState('welcome');
  const [currentText, setCurrentText] = useState(null);
  const [jumpTarget, setJumpTarget] = useState(null);   // {segId, sentId, wIdx, view, focus} — open reader here
  const [readerOrigin, setReaderOrigin] = useState(null);  // {view} — the reader view 跳到複習 launched from
  const [resumeReview, setResumeReview] = useState(false);  // resume the study session
  const [resumeWord, setResumeWord] = useState(null);  // land review on this word (pre-reveal)
  const [resumeDialect, setResumeDialect] = useState(null);  // review scope on resume

  const due = getLearningWords().filter(isDue).length;   // 複習 pill badge (matches the phone)

  function openText(text) {
    setJumpTarget(null);
    setReaderOrigin(null);
    setCurrentText(text);
    setScreen('reader');
  }

  // 跳到故事 (review → reader). Both directions land in WORD-VIEW (popup) on the word:
  //  review-origin → 片 word-view (segment coords); reader-origin → the view I started in,
  //  word-view on the EXACT word I launched 跳到複習 from (the captured anchor coords).
  function jumpToStory(story, segId, dia, word, wi, sentId) {
    setResumeWord(word || null);
    setResumeDialect(dia || null);
    setCurrentText(story);
    if (readerOrigin) {
      setJumpTarget({ segId, sentId, view: readerOrigin.view, anchorId: readerOrigin.anchorId, anchorWIdx: readerOrigin.anchorWIdx, focus: true });
      setReaderOrigin(null);
    } else {
      setJumpTarget({ segId, sentId, view: 'segment', anchorId: segId, anchorWIdx: wi, focus: true });
    }
    setScreen('reader');
  }

  // 跳到複習 (reader → review): land on THIS word's card, pre-reveal. Remember the origin
  // view AND the exact word coords (item id + word index as rendered) so the return
  // (跳到故事) re-opens word-view on the same word, in that view.
  function jumpToReview(word, dia, originView, anchorId, anchorWIdx) {
    setReaderOrigin(originView ? { view: originView, anchorId, anchorWIdx } : null);
    setResumeDialect(dia || null);
    setResumeWord(word || null); setResumeReview(true);
    setCurrentText(null); setScreen('srs');
  }

  function goBack() {
    setJumpTarget(null);
    setCurrentText(null);
    setReaderOrigin(null);
    setScreen('library');
  }

  if (!ready) return <div className="app-loading">…</div>;
  if (!user) return <Login />;

  return (
    <div className="app" style={{ '--fs': fontScale }}>
      <main className="app-main">
        {screen === 'welcome' && <Welcome onEnter={() => setScreen('library')} />}
        {screen === 'library' && (
          <>
            <header className="ph-header">
              <button className="ph-home" onClick={() => setScreen('welcome')}>3S4T賽德克語聖經</button>
              <nav className="ph-pills">
                <button className="ph-pill"
                  onClick={() => { setCurrentText(null); setReaderOrigin(null); setResumeReview(false); setResumeWord(null); setResumeDialect(null); setScreen('srs'); }}
                >複習{due ? ` ${due}` : ''}</button>
                <button className="ph-pill" onClick={() => setScreen('settings')}>設定</button>
                <button className="ph-pill" onClick={() => setScreen('about')}>關於</button>
              </nav>
            </header>
            <TextBrowser onSelect={openText} />
          </>
        )}
        {screen === 'reader' && currentText && <Reader text={currentText} target={jumpTarget} onBack={goBack} onJumpToReview={jumpToReview} />}
        {screen === 'srs' && (
          <SRSReview onBack={goBack} onJump={jumpToStory} resume={resumeReview} resumeWord={resumeWord}
            resumeDialect={resumeDialect} onResumed={() => { setResumeReview(false); setResumeWord(null); }} />
        )}
        {screen === 'about' && <About onBack={goBack} />}
        {screen === 'settings' && (
          <SettingsScreen onBack={goBack} fontScale={fontScale} setFontScale={setFontScale}
            version={BUILD_ID} user={user} logout={logout} />
        )}
      </main>
      <div className="build-badge" title="loaded bundle build id">{BUILD_ID}</div>
    </div>
  );
}

export default function App() {
  // Static build: progress mirrors to the cloud automatically
  // (utils/cloudSync.js) — a fresh install pulls it back by itself.
  useEffect(() => {
    if (!STATIC) return undefined;
    const stopSync = startCloudSync();
    const stopVocabSync = startVocabSync();
    return () => { stopSync(); stopVocabSync(); };
  }, []);

  return (
    <AuthProvider>
      <AppInner />
    </AuthProvider>
  );
}
